// ════════════════════════════════════════════════════════════
//  online/keeper.js — THE KEEPER: the League never skips a fight
// ════════════════════════════════════════════════════════════
//  There is no server program. Every signed-in screen runs the keeper a
//  little at a time: it walks the hours from the cursor forward and, for
//  every hour whose bouts are all fought (or scratched past :53), makes
//  sure each has a spec, a seed, a result (a headless replay if nobody
//  watched — about 0.2 s each) and a LEDGER ROW. When the whole hour is
//  in, the cursor moves on. Hour by hour, in order, because who fights
//  next depends on every result before (ledger.js).
//
//  Hours nobody was online for are fought the first time anybody looks.
//
//  ── THE FAST-FORWARD (caP'n, 2026-10-16: "yes very 4xS <3") ──────
//  An hour that ended more than PAPER_AFTER ago and was never locked (so
//  no screen saw a single bell of it) is decided ON PAPER: the same card,
//  its first PAPER_BOUTS bouts, each winner drawn from the bout's own id,
//  weighted by the two ratings (Willie's line). No match is written and
//  nothing is replayed, only the lock and the ledger rows (why "paper", no
//  time, no Anima — a paper win never counts as a fast one). Every screen
//  draws the same winner, so two keepers racing write the same rows.
//  ⚑ The real cure is a server that runs every hour (pinned by caP'n as
//  very important, for later).
// ════════════════════════════════════════════════════════════
import { CALL, hourOf, hourStart, hourInfo, HOUR_MS, eloP, mulberry, strSeed, standings, statOf, lastBellOf, paperBell, knownRows, paperAt, resolveBound, boutKey } from "./circuit.js";
import { ledgerOf, leagueOf, cursorOf, advanceCursor, hourPath } from "./ledger.js";
import { loadHour, planOf, entrantOf, boutId } from "./stage.js";
import { rowOf } from "./rows.js";
export { rowOf };

const GRACE = 25e3;
// an hour that STILL can't be finished this long after it ended (a broken spec, a fight that can't be
// replayed) is recorded as far as it goes and passed — one bad fight must never freeze the League forever
export const GIVE_UP_MS = 2 * 3600e3;
export const PAPER_AFTER = 3 * HOUR_MS;   // ⟵ DIAL — an unwatched hour older than this is decided on paper
const PAPER_AHEAD = 24;                   // hour nodes read at once while fast-forwarding

/** When every bout of hour h is certainly over (the last bell is :53, a bout is called at 5:00). */
export const hourOverAt = (h) => lastBellOf(h) + CALL * 1000 + GRACE;

export async function record(net, f, info) {
  if (!info.counts) return null;   // preseason: nothing scores
  const row = rowOf(f, info);
  return row ? ledgerOf(net).add(row) : null;
}

/** A paper bout's winner: 0 or 1, drawn from its id, weighted by the ratings. Same on every screen. */
export function paperWinner(id, A, B, S) {
  if (!A || !B) return A ? 0 : 1;
  const p = eloP(statOf(S, A.key).elo, statOf(S, B.key).elo);
  return mulberry(strSeed(`paper:${id}`))() < p ? 0 : 1;
}

/**
 * Hour h, on paper: lock it, draw its first PAPER_BOUTS bouts, write the rows. The writes are not
 * waited on (pending collects them; keep() moves the cursor once a whole batch is in).
 * Resolves false if the hour can't be planned yet (its week's leagues aren't ready).
 */
export async function paperHour(net, h, now, node, pending) {
  const info = hourInfo(h);
  const local = [];
  const plan = await planOf(net, h, now, { paper: true, node, pending: local });
  if (!plan.locked || !plan.card) return false;
  const league = await leagueOf(net, info.week);
  if (!league) return false;
  // the ratings going into this hour — only what happened BEFORE it (same as the card), so every screen agrees
  const S = standings(knownRows(await ledgerOf(net).rows(info.week), h), league.elo);
  const rows = [], won = new Map();   // won: a Qualifier's winners so far — its later rounds (late-bound, circuit.js isBound) are read off them, in card order
  // each STAGE's first PAPER_BOUTS (V3 runs two side by side; before it there's one, so this is the card's first PAPER_BOUTS)
  plan.card.forEach((b, j) => {
    const n = paperAt(plan.card, j);   // closers always, then the first fillers (circuit.js)
    if (n < 0) return;
    const sd = resolveBound(b, won);
    if (!sd) return;
    const A = entrantOf(sd.a, plan.pb, plan.ch), B = entrantOf(sd.b, plan.pb, plan.ch);
    if (!A || !B) return;
    const f = { ...b, a: sd.a, b: sd.b, id: boutId(h, j), j, A, B, startAt: paperBell(h, n), endAt: paperBell(h, n), phase: "done", why: "paper" };
    f.winner = paperWinner(f.id, A, B, S);
    if (b.k === "cup") won.set(boutKey(b.c, b.r, b.i), f.winner === 0 ? sd.a : sd.b);
    if (info.counts) { const row = rowOf(f, info); if (row) rows.push(row); }
  });
  local.push(ledgerOf(net).addMany(info.week, rows));
  const done = Promise.all(local);
  done.catch(() => {});   // seen by keep()'s flush, never an unhandled rejection
  pending.push(done);
  return true;
}

/**
 * Do a slice of catching up. budgetMs bounds the work (a replay is ~0.2 s, so a slice overshoots by
 * at most one hour's bouts). Resolves { cursor, behind } — behind = hours still to fight (0 = caught up).
 */
export async function keep(net, now, { budgetMs = 1200, paperMs = 3000 } = {}) {
  const t0 = Date.now();
  let h = (await cursorOf(net)) + 1;
  const pending = [], nodes = new Map();
  let week = null, breath = Date.now(), paperTo = null;
  // the paper writes go out in order; once a batch is all in, the cursor jumps to its last hour
  const flush = async () => {
    const all = pending.splice(0), to = paperTo;
    paperTo = null;
    await Promise.all(all);
    if (to != null) await advanceCursor(net, to);
  };
  try {
    // an hour can finish early (its last bout ended before :58) — so the current hour is tried too
    while (hourStart(h) + 60e3 <= now) {
      const info = hourInfo(h);
      // ── the fast-forward: an old hour no screen ever locked is decided on paper ──
      if (info.counts && now - hourOverAt(h) > PAPER_AFTER) {
        if (!nodes.has(h)) {
          const hs = [];
          for (let x = h; x < h + PAPER_AHEAD && now - hourOverAt(x) > PAPER_AFTER; x++) hs.push(x);
          const got = await Promise.all(hs.map((x) => net.get(hourPath(x))));
          hs.forEach((x, i) => nodes.set(x, got[i] || null));
        }
        const node = nodes.get(h);
        if (!node || !node.lock || node.lock.paper) {
          if (week !== null && week !== info.week) await flush();   // a new week's leagues are built from the server's record
          week = info.week;
          if (!(await paperHour(net, h, now, node, pending))) break;
          paperTo = h;
          h++;
          if (Date.now() - t0 > paperMs) break;
          // paper is quick, but hundreds of hours in a row would hold the screen: let a frame through
          if (Date.now() - breath > 25) { await new Promise((r) => setTimeout(r, 0)); breath = Date.now(); }
          continue;
        }
      }
      await flush();   // a real hour reads the server: everything before it must be there
      if (info.counts) {
        const H = await loadHour(net, { hour: h, now });
        if (!H.locked) break;                                   // its lock is still to come (it's :00:0x) — next pass
        const open = H.bouts.filter((f) => f.phase !== "done" && f.phase !== "scratched");
        const stuck = now > hourOverAt(h) + GIVE_UP_MS;
        if (open.length && !stuck) break;                       // something's still on (or can't be decided yet)
        if (stuck && open.length) {
          console.warn(`[league keeper] hour ${h} couldn't be finished — passing it`); // eslint-disable-line no-console
          // void what never finished: bets refund, and no screen can record it behind the cursor later
          const at = hourStart(h);
          for (const f of open) await net.txn(`${f.path}/spec`, (cur) => (cur == null ? { void: true, keys: [f.A && f.A.key, f.B && f.B.key].filter(Boolean), createdAt: at } : cur.void ? undefined : { ...cur, void: true })).catch(() => {});
        }
        for (const f of H.bouts) if (f.phase === "done") await record(net, f, info);
      }
      await advanceCursor(net, h);
      h++;
      if (Date.now() - t0 > budgetMs) break;
    }
  } finally { await flush(); }
  return { cursor: h - 1, behind: Math.max(0, hourOf(now) - h) };
}
